// src/encounter/template-load-notice.tsx
// Contract G11 — what the encounter says about how its template LOADED, above the step canvas, on
// every step. Each line shows only when true:
//   1. The template could not be READ (not "no template matched"): an error banner with Retry.
//      A/P and Wrap-up stay usable; HPI · Risk · ROS · PE say they could not load; signing waits for
//      the template, because the note's saved answers were never loaded into the form.
//   2. The visit's specialty could not be read, so the template request went out without it
//      (PROD-2701's fallback): one line saying so, instead of passing it silently.
// Nothing renders when the template loaded with everything it was asked for.
import { Banner, Button } from '@scripsteam/scrips-signal-ds';

// Token classes only (the DS shadow-component guard allows no inline styles here).
const WRAP = 'flex flex-col gap-2';

export interface TemplateLoadNoticeProps {
  /** The template read failed (the hook's `isError`): there is no template on screen. */
  failed: boolean;
  /** Re-read the template (the hook's `retry`). */
  onRetry: () => void;
  /** A re-read is in flight — Retry waits for it. */
  retrying?: boolean;
  /** The template was picked from the axes with no specialty because the specialty lookup failed. */
  chosenWithoutSpecialty: boolean;
  /** TS-4: the template was picked without the visit's type because that lookup failed. */
  chosenWithoutVisitType?: boolean;
}

/** One sentence for whichever of the visit's facts could not be loaded (both come from one read). */
function missingFactsSentence(specialty: boolean, visitType: boolean): string {
  if (specialty && visitType) {
    return 'The visit’s specialty and type couldn’t be loaded, so this template was chosen without them.';
  }
  return specialty
    ? 'The visit’s specialty couldn’t be loaded, so this template was chosen without it.'
    : 'The visit’s type couldn’t be loaded, so this template was chosen without it.';
}

export function TemplateLoadNotice({
  failed,
  onRetry,
  retrying,
  chosenWithoutSpecialty,
  chosenWithoutVisitType = false,
}: TemplateLoadNoticeProps) {
  if (!failed && !chosenWithoutSpecialty && !chosenWithoutVisitType) return null;
  return (
    <div data-testid="template-load-notice" className={WRAP}>
      {failed && (
        <Banner
          variant="error"
          title="Couldn’t load the encounter template"
          action={
            <Button variant="secondary" size="sm" onClick={onRetry} disabled={retrying}>
              {retrying ? 'Retrying…' : 'Retry'}
            </Button>
          }
        >
          HPI, Risk, ROS and PE come from the template and can’t be shown until it loads. Assessment &amp; Plan and
          Wrap-up still work; signing waits until the template has loaded.
        </Banner>
      )}
      {(chosenWithoutSpecialty || chosenWithoutVisitType) && (
        <Banner variant="warning" title="The template was chosen without some visit details">{missingFactsSentence(chosenWithoutSpecialty, chosenWithoutVisitType)}</Banner>
      )}
    </div>
  );
}
